import { combinedStatus } from "./journey";
import type { EvidenceStatus, TestEvidence } from "./types";

/**
 * How much a browser test can currently tell QA.
 *
 * The inventory previously ranked tests worst-first and truncated the list, so
 * a catalog where most specs are in no CI selection at all rendered as page
 * after page of "Not run" and hid every passing test below the cut. "Not run"
 * and "ran and failed" are completely different facts and QA needs them
 * separated before anything else:
 *
 * - `verified` — produced a result on the headline build.
 * - `earlier`  — produced a result, but on a superseded build.
 * - `excluded` — declared out of scope (`fixme` / `skipped`). A declaration,
 *                never an observation, so it is not a coverage gap.
 * - `never`    — no run has ever reported this test. It is in the repository
 *                but no CI selection executes it.
 */
export type CoverageTier = "verified" | "earlier" | "excluded" | "never";

export function coverageTier(test: TestEvidence, exactSha?: string): CoverageTier {
  if (test.status === "fixme" || test.status === "skipped") return "excluded";
  if (!test.lastRunSha) return "never";
  return exactSha && test.lastRunSha === exactSha ? "verified" : "earlier";
}

export interface CoverageSummary {
  tests: number;
  verified: number;
  earlier: number;
  excluded: number;
  never: number;
  failing: number;
  specs: number;
  specsExercised: number;
}

export interface SpecGroup {
  path: string;
  /** The spec path with the shared e2e prefix removed, for display. */
  name: string;
  domain: string;
  tests: TestEvidence[];
  status: EvidenceStatus;
  verified: number;
  earlier: number;
  excluded: number;
  never: number;
  /** True when any run has ever reported a result for this spec. */
  exercised: boolean;
  lastRunSha?: string;
  durationMs: number;
}

const E2E_PREFIXES = [
  "frontend/admin-portal/tests/e2e/",
  "admin-portal/tests/e2e/",
  "tests/e2e/",
];

export function specName(path: string): string {
  for (const prefix of E2E_PREFIXES) {
    if (path.startsWith(prefix)) return path.slice(prefix.length);
  }
  return path;
}

/**
 * Order specs by what a QA reader should act on first: current breakage, then
 * evidence that has gone out of date, then everything currently proving out.
 * Specs no selection runs sort last — they are a coverage question, not a
 * correctness one, and they must never bury the specs that did run.
 */
const specPriority: EvidenceStatus[] = [
  "failed",
  "flaky",
  "stale",
  "passed",
  "missing",
  "fixme",
  "skipped",
];

export function groupBySpec(tests: TestEvidence[], exactSha?: string): SpecGroup[] {
  const groups = new Map<string, TestEvidence[]>();
  for (const test of tests) {
    groups.set(test.path, [...(groups.get(test.path) ?? []), test]);
  }
  return Array.from(groups.entries())
    .map(([path, specTests]) => {
      const tiers = specTests.map((test) => coverageTier(test, exactSha));
      const exercised = tiers.some((tier) => tier === "verified" || tier === "earlier");
      // A spec nothing has run has no verdict to combine — every member is
      // `missing`, and combinedStatus would report that as the spec's status.
      // That is right, but the tier counters below are what the view reads.
      return {
        path,
        name: specName(path),
        domain: specTests[0]?.domain ?? "Unclassified",
        tests: [...specTests].sort((left, right) => left.title.localeCompare(right.title)),
        status: combinedStatus(specTests.map((test) => test.status)),
        verified: tiers.filter((tier) => tier === "verified").length,
        earlier: tiers.filter((tier) => tier === "earlier").length,
        excluded: tiers.filter((tier) => tier === "excluded").length,
        never: tiers.filter((tier) => tier === "never").length,
        exercised,
        lastRunSha: specTests.find((test) => test.lastRunSha)?.lastRunSha,
        durationMs: specTests.reduce((total, test) => total + (test.durationMs ?? 0), 0),
      };
    })
    .sort(
      (left, right) =>
        specPriority.indexOf(left.status) - specPriority.indexOf(right.status) ||
        left.name.localeCompare(right.name),
    );
}

export function summarizeCoverage(tests: TestEvidence[], exactSha?: string): CoverageSummary {
  const tiers = tests.map((test) => coverageTier(test, exactSha));
  const specs = groupBySpec(tests, exactSha);
  return {
    tests: tests.length,
    verified: tiers.filter((tier) => tier === "verified").length,
    earlier: tiers.filter((tier) => tier === "earlier").length,
    excluded: tiers.filter((tier) => tier === "excluded").length,
    never: tiers.filter((tier) => tier === "never").length,
    failing: tests.filter((test) => test.status === "failed" || test.status === "flaky").length,
    specs: specs.length,
    specsExercised: specs.filter((spec) => spec.exercised).length,
  };
}

export interface DomainCoverage {
  name: string;
  tests: number;
  verified: number;
  never: number;
}

/** Per-area coverage, worst-covered first, so the thinnest areas are visible. */
export function coverageByDomain(tests: TestEvidence[], exactSha?: string): DomainCoverage[] {
  const domains = new Map<string, DomainCoverage>();
  for (const test of tests) {
    const name = test.domain || "Unclassified";
    const entry = domains.get(name) ?? { name, tests: 0, verified: 0, never: 0 };
    const tier = coverageTier(test, exactSha);
    entry.tests += 1;
    if (tier === "verified") entry.verified += 1;
    if (tier === "never") entry.never += 1;
    domains.set(name, entry);
  }
  return Array.from(domains.values()).sort(
    (left, right) =>
      left.verified / left.tests - right.verified / right.tests ||
      left.name.localeCompare(right.name),
  );
}

export function filterSpecs(specs: SpecGroup[], query: string, domain: string): SpecGroup[] {
  const needle = query.trim().toLocaleLowerCase();
  return specs.filter((spec) => {
    const haystack = `${spec.name} ${spec.domain} ${spec.tests
      .map((test) => test.title)
      .join(" ")}`.toLocaleLowerCase();
    return (!needle || haystack.includes(needle)) && (domain === "all" || spec.domain === domain);
  });
}
